import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Collapse
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Check,
  ExpandLess,
  ExpandMore,
  Route,
  RadioButtonChecked
} from '@mui/icons-material';
import ApiContext from 'contexts/ApiContext';

// Connector that mirrors the Crane mockup: dark navy line between completed
// stages, grey for everything ahead of the active step. The active stage's
// inbound segment is also dark navy so the path reads as "you are here".
const ProgressConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 14,
    left: 'calc(-50% + 18px)',
    right: 'calc(50% + 18px)'
  },
  [`&.${stepConnectorClasses.active}, &.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.dark
    }
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: 2,
    borderColor: theme.palette.grey[300],
    transition: 'border-color 200ms ease'
  }
}));

interface FlowStatus {
  uid: string;
  _id?: string;
  id?: string;
  name: string;
  order?: number;
}

interface Props {
  teamUid?: string;
  /** The current key status — either an _id, id, or full object. */
  currentKeyStatus?: any;
  /**
   * Optional human-readable name of the current status. Used as a fallback
   * match against the FlowStatus list when ID-based matching fails (the
   * transaction API sometimes serves keyStatus as the raw ObjectId while
   * the /flow/status/team endpoint returns the populated record without an
   * _id — name match keeps the stepper aligned in that case).
   */
  currentKeyStatusName?: string;
  /** Display fallback if FlowStatus is missing or empty. */
  fallbackLabel?: string;
}

/**
 * Renders the team's configured workflow stages as a horizontal stepper.
 * Pulls the FlowStatus list from /flow/status/team/:uid and orders by
 * `order` (insertion order is the fallback). Marks the current stage
 * active; everything before it is completed; everything after is muted.
 */
const ProgressTrackerStepper: React.FC<Props> = ({
  teamUid,
  currentKeyStatus,
  currentKeyStatusName,
  fallbackLabel
}) => {
  const apiContext = useContext(ApiContext);
  const [statuses, setStatuses] = useState<FlowStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!apiContext) {
      setLoading(false);
      return;
    }
    if (!teamUid) {
      // Core tier hasn't resolved yet — teamUid will show up once it does
      // (this effect re-runs since it's in the dependency array below).
      // Deliberately NOT calling setLoading(false) here: doing so briefly
      // rendered the "no statuses configured" fallback before the real
      // fetch ever ran, instead of the loading spinner.
      return;
    }
    (async () => {
      try {
        // getNodeStatus is the canonical reporting-status fetch used by the
        // transactions list / Kanban — re-using it keeps the source of truth
        // for stages identical across the surface.
        const fetcher =
          (apiContext as any).getNodeStatus || (apiContext as any).getFlowStatusByTeam;
        const res: any = await fetcher?.(teamUid);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        if (!cancelled) setStatuses(list as FlowStatus[]);
      } catch {
        if (!cancelled) setStatuses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiContext, teamUid]);

  const orderedStatuses = useMemo(() => {
    if (!statuses?.length) return [];
    // Natural-numeric sort by name — the canonical ordering used by the
    // Kanban transaction view (see kanbantransactionview.tsx:295). Status
    // names follow the convention "N. SOMETHING" (e.g. "1. PLANNING /
    // READY TO BOOK"), so the embedded numeric prefix drives the order.
    // `numeric: true` makes "10. X" sort after "2. X" instead of before.
    // Falls back to an explicit `order` field when present, so teams that
    // *do* set ordering still take precedence.
    return [...statuses].sort((a, b) => {
      const ao = a.order;
      const bo = b.order;
      if (typeof ao === 'number' && typeof bo === 'number' && ao !== bo) {
        return ao - bo;
      }
      return (a.name || '').localeCompare(b.name || '', undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
  }, [statuses]);

  const activeIndex = useMemo(() => {
    if (!orderedStatuses.length) return -1;
    const target = (() => {
      if (typeof currentKeyStatus === 'string') return currentKeyStatus;
      return currentKeyStatus?._id || currentKeyStatus?.id || currentKeyStatus?.uid || '';
    })();
    // 1. ID-based match against the FlowStatus list.
    if (target) {
      const idIdx = orderedStatuses.findIndex(
        (s) => s.uid === target || s._id === target || (s as any).id === target
      );
      if (idIdx >= 0) return idIdx;
    }
    // 2. Name-based fallback — the transaction payload sometimes only carries
    //    the human-readable name (displayKeyStatus / keyStatus.name) while the
    //    list endpoint omits _id. Compare case-insensitively to stay forgiving.
    const namedTarget = (() => {
      if (currentKeyStatusName && typeof currentKeyStatusName === 'string') return currentKeyStatusName;
      if (currentKeyStatus && typeof currentKeyStatus === 'object' && currentKeyStatus.name) {
        return String(currentKeyStatus.name);
      }
      return '';
    })().trim().toLowerCase();
    if (namedTarget) {
      const nameIdx = orderedStatuses.findIndex(
        (s) => (s.name || '').trim().toLowerCase() === namedTarget
      );
      if (nameIdx >= 0) return nameIdx;
    }
    return -1;
  }, [orderedStatuses, currentKeyStatus, currentKeyStatusName]);

  // We intentionally never return null here. The Progress Tracker is a core
  // anchor on the details page and the user expects to see it even when the
  // team has no FlowStatus configured or the API is still loading — we just
  // swap the body content for a sensible placeholder.

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden'
      }}
    >
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          bgcolor: 'primary.dark',
          color: '#fff',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Route sx={{ color: '#fff', fontSize: 18 }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#fff' }}
          >
            Progress Tracker
          </Typography>
          {activeIndex >= 0 && orderedStatuses[activeIndex] && (
            <Typography
              variant="caption"
              sx={{ ml: 1.5, color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}
            >
              Currently: {orderedStatuses[activeIndex].name}
            </Typography>
          )}
        </Box>
        <IconButton size="small" sx={{ color: '#fff' }}>
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
      <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : orderedStatuses.length === 0 ? (
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              bgcolor: 'background.paper'
            }}
          >
            <Box
              sx={(theme) => ({
                width: 30,
                height: 30,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: theme.palette.primary.dark,
                color: '#fff',
                border: `3px solid ${theme.palette.primary.light}`,
                boxShadow: `0 0 0 4px ${theme.palette.primary.light}33`
              })}
            >
              <RadioButtonChecked sx={{ fontSize: 14 }} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                Current Status
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                {fallbackLabel || 'In Progress'}
              </Typography>
            </Box>
          </Box>
        ) : (
        <Box sx={{ px: 3, py: 2.5, overflowX: 'auto' }}>
          <Stepper
            alternativeLabel
            activeStep={activeIndex >= 0 ? activeIndex : orderedStatuses.length}
            connector={<ProgressConnector />}
          >
            {orderedStatuses.map((s, idx) => {
              const isCompleted = activeIndex >= 0 && idx < activeIndex;
              const isActive = idx === activeIndex;
              const IconComp = () => (
                <Box
                  sx={(theme) => ({
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 200ms ease',
                    bgcolor: isActive || isCompleted ? theme.palette.primary.dark : theme.palette.grey[200],
                    color: isActive || isCompleted ? '#fff' : theme.palette.grey[500],
                    border: isActive ? `3px solid ${theme.palette.primary.light}` : 'none',
                    boxShadow: isActive ? `0 0 0 4px ${theme.palette.primary.light}33` : 'none'
                  })}
                >
                  {isCompleted ? (
                    <Check sx={{ fontSize: 16 }} />
                  ) : isActive ? (
                    <RadioButtonChecked sx={{ fontSize: 14 }} />
                  ) : (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'currentColor' }} />
                  )}
                </Box>
              );
              return (
                <Step key={s.uid || s._id || idx} completed={isCompleted} active={isActive}>
                  <StepLabel StepIconComponent={IconComp}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: isActive ? 700 : 500,
                        color: isActive
                          ? 'primary.dark'
                          : isCompleted
                            ? 'text.primary'
                            : 'text.disabled',
                        textTransform: 'none',
                        lineHeight: 1.3
                      }}
                    >
                      {s.name}
                    </Typography>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
        )}
      </Box>
      </Collapse>
    </Paper>
  );
};

export default ProgressTrackerStepper;
